import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { authFetch } from '@/lib/authFetch';
import GenerationView from '@/components/GenerationView';
import AccountDashboard from '@/components/dashboard/AccountDashboard';
import SEOHead from '@/components/SEOHead';
import { DASHBOARD_ROUTE } from '@/lib/SignedOutOnly';

/**
 * The build workspace — its own route, its own screen.
 *
 * This used to render inside AIBuilderSection as a `fixed inset-0 z-[100]`
 * overlay, so the home page stayed mounted and scrolling underneath it. That
 * left the URL on `/`, meant Back closed the whole workspace instead of
 * stepping through it, made a refresh drop you on the marketing page, and gave
 * the navbar and footer a chance to bleed through at the edges.
 *
 * Two ways in:
 *   /build              — a fresh build; the prompt (and any attached image)
 *                         arrive in router state
 *   /build/:websiteId   — an existing site; loaded from the API so the URL is
 *                         shareable and survives a refresh
 *
 * A fresh build adopts the second form as soon as the server reports a saved
 * row, so refreshing mid-generation returns to the run rather than to `/`.
 * Both forms are ONE route with an optional param — see App.jsx for why.
 */
export default function Workspace() {
  const { websiteId } = useParams();
  const { state, search } = useLocation();
  const navigate      = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Frozen at mount, with one exception below. What kind of visit this is must
  // not change when the URL gains an id part-way through: without this, adopting
  // the id would make the workspace think it had arrived cold, fetch the
  // half-written record, and reset the editor out from under a running run.
  const freshPromptRef = useRef(state?.prompt ?? '');
  const freshImageRef  = useRef(state?.image ?? null);
  // The "Plan first" switch as it stood when Build was pressed. Anything that
  // did not say — an older link, a resumed prompt — gets the plan, the default.
  const freshPlanFirstRef = useRef(state?.planFirst !== false);

  // The exception: a build started from *inside* the workspace — the dashboard's
  // own prompt box — navigates to /build with a new prompt while this component
  // stays mounted. Frozen refs alone meant the prompt was never picked up, the
  // visit looked like "no id, no prompt, not the dashboard", and the guard below
  // bounced the user to the marketing page instead of building anything.
  //
  // Read during render rather than in an effect because that guard runs in the
  // same commit and would fire first on a stale value.
  if (state?.prompt && state.prompt !== freshPromptRef.current) {
    freshPromptRef.current = state.prompt;
    freshImageRef.current  = state.image ?? null;
    freshPlanFirstRef.current = state.planFirst !== false;
  }
  const isFreshBuild = !!freshPromptRef.current;

  // Which id this component put in the address bar itself. Adoption must not be
  // mistaken for the user navigating to a different site.
  const adoptedIdRef = useRef(null);
  // Bumped only for a genuine change of site, to remount the editor beneath.
  const [runKey, setRunKey] = useState(0);
  const shownIdRef = useRef(websiteId);
  // The prompt the mounted run was started with. A second build launched from
  // the dashboard navigates /build → /build, so the id never changes and the
  // prompt is the only thing that says "this is a different run".
  const shownPromptRef = useRef(state?.prompt ?? '');

  // A third way in: /Dashboard redirects here so the dashboard tab can be the
  // dashboard. That arrives with no site id and no prompt, which the guards
  // below would otherwise treat as "nothing to do" and bounce to the homepage.
  // Payment gateways land here the same way (…?success=flw), so those count too.
  const params = new URLSearchParams(search || '');
  const isDashboardVisit = params.get('view') === 'dashboard'
    || params.has('success') || params.has('refresh');

  // A site handed over from the dashboard list renders immediately; without one
  // (refresh, pasted link) we fetch before showing the editor anything.
  const [website, setWebsite] = useState(state?.website ?? null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(!!websiteId && !isFreshBuild && !state?.website);

  /**
   * Follow a genuine change of site.
   *
   * `/build/:websiteId?` is one route, so moving between sites never remounts
   * this component — that is deliberate, and it is what keeps a running build
   * alive while it adopts its id. The cost was that opening a *different* site
   * did nothing at all: `website` still held the previous record, so the fetch
   * below short-circuited on it and the editor kept showing the old site.
   *
   * So the two cases are told apart explicitly. Adoption (an id this component
   * asked for) is ignored. Anything else resets the record and bumps `runKey`,
   * which remounts the editor with the new site.
   *
   * Declared before the redirect guards so a reset lands first.
   */
  useEffect(() => {
    const nextPrompt = state?.prompt ?? '';
    const idChanged = websiteId !== shownIdRef.current;
    // Build from the dashboard lands on the same path it was already on, so
    // without this the run was never started: same id, no reset, no remount.
    const promptChanged = !!nextPrompt && nextPrompt !== shownPromptRef.current;

    if (!idChanged && !promptChanged) return;
    if (idChanged && websiteId && websiteId === adoptedIdRef.current) {
      shownIdRef.current = websiteId;   // our own doing; leave the run alone
      return;
    }

    shownIdRef.current = websiteId;
    shownPromptRef.current = nextPrompt;
    adoptedIdRef.current = null;
    // What kind of visit this is now. The fresh-build refs belong to the run
    // that was on screen, and they were never cleared, so every later visit
    // looked like a fresh build too. Opening a site then waited on a fetch
    // that skipped itself, leaving "Opening workspace…" up for good. And a
    // return to the dashboard remounted the editor still holding the old
    // prompt, which starts that build all over again.
    freshPromptRef.current = nextPrompt;
    freshImageRef.current = state?.image ?? null;
    freshPlanFirstRef.current = state?.planFirst !== false;
    setWebsite(state?.website ?? null);
    setLoadError('');
    // A record is only worth waiting for when there is an id and nothing to
    // show for it yet; a fresh build has its prompt and needs no fetch.
    setLoading(!!websiteId && !state?.prompt && !state?.website);
    setRunKey((k) => k + 1);
  }, [websiteId, state]);

  // Nothing to work on — someone refreshed /build before the run had saved, so
  // the prompt that lived only in router state is gone. Back to the box.
  useEffect(() => {
    if (authLoading || websiteId || isFreshBuild || isDashboardVisit) return;
    navigate('/', { replace: true });
  }, [authLoading, websiteId, isFreshBuild, isDashboardVisit, navigate]);

  // The workspace spends credits and saves against an account, so it is not a
  // place to be without one.
  useEffect(() => {
    if (authLoading || user) return;
    navigate('/', { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!websiteId || isFreshBuild || website || !user) return;
    let cancelled = false;

    (async () => {
      try {
        // authFetch resolves to the parsed body and throws on a non-2xx.
        const data = await authFetch(`/api/websites/${websiteId}`);
        if (!cancelled) setWebsite(data.website);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'That site could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [websiteId, isFreshBuild, website, user]);

  /**
   * Put the new row's id in the address bar the moment it exists.
   *
   * `replace` rather than `push` so Back still leaves the workspace instead of
   * stepping onto a bare /build that no longer has a prompt to run.
   */
  const handleWebsiteCreated = useCallback((newId) => {
    if (!newId || websiteId) return;
    // Claimed before navigating, so the effect above recognises this id as our
    // own and leaves the running generation untouched.
    adoptedIdRef.current = newId;
    shownIdRef.current = newId;
    navigate(`/build/${newId}`, { replace: true });
  }, [websiteId, navigate]);

  // The redirects above run in effects, i.e. after this render. Without this
  // guard the workspace mounts for a frame with nothing to build, booting the
  // whole generation stack only to tear it straight back down.
  const hasNothingToDo = !user || (!websiteId && !isFreshBuild && !isDashboardVisit);

  if (authLoading || loading || hasNothingToDo) {
    return (
      <div className="fixed inset-0 bg-zinc-950 flex flex-col items-center justify-center gap-3 text-zinc-500">
        <div className="w-8 h-8 rounded-full border-2 border-zinc-800 border-t-orange-500 animate-spin" />
        <span className="text-xs font-semibold uppercase tracking-widest">Opening workspace…</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="fixed inset-0 bg-zinc-950 flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-white font-semibold">{loadError}</p>
        <button
          onClick={() => navigate('/')}
          className="px-5 py-2.5 rounded-xl bg-white text-zinc-950 font-bold text-sm hover:bg-zinc-200 transition-colors"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  // The account dashboard: no site open and nothing being built. It is its
  // own screen — sidebar, sites, marketplace, earnings, account — and has no
  // Preview / Dashboard / Code tabs, because there is no site for them to be
  // about. Those tabs belong to the site workspace below.
  if (!websiteId && !isFreshBuild && isDashboardVisit) {
    return (
      <>
        <SEOHead title="Dashboard" description="Your BuildersLab sites, earnings and account." noIndex />
        <AccountDashboard />
      </>
    );
  }

  const requestedTab = params.get('tab');
  /** @type {'preview'|'dashboard'|'code'} */
  const initialTab = requestedTab === 'dashboard' || requestedTab === 'code' ? requestedTab : 'preview';
  return (
    <>
      <SEOHead
        title={website?.title ? `${website.title} — Workspace` : 'Workspace'}
        description="Build and refine your site with BuildersLab AI."
        noIndex
      />
      <GenerationView
        key={runKey}
        prompt={isFreshBuild ? freshPromptRef.current : (website?.prompt || '')}
        initialImage={isFreshBuild ? freshImageRef.current : null}
        planFirst={freshPlanFirstRef.current}
        existingWebsite={isFreshBuild ? undefined : (website || undefined)}
        onWebsiteCreated={handleWebsiteCreated}
        // A link can open a site straight on its dashboard or its code.
        initialTab={initialTab}
        initialFollowUp={isFreshBuild ? '' : (state?.draft || '')}
        // Back goes to the dashboard, which is where people expect it to go.
        // It used to step back through browser history, which lands somewhere
        // different depending on how the workspace was reached: the marketing
        // page, a site opened earlier, a /build entry with no prompt left.
        onBack={() => navigate(DASHBOARD_ROUTE)}
      />
    </>
  );
}
